// Lightweight per-route SEO hook. Avoids the react-helmet dependency and
// keeps the DOM up to date with the right canonical URL, robots directive,
// title, description, and OG/Twitter image on client-side route changes.
//
// Usage:
//   useSEO({
//     title: "Pricing — EpisodeKit",
//     description: "...",
//     path: "/pricing",          // canonical path; absolute URL is derived
//     image: "/og/episodekit-og-pricing.jpg",  // page-specific OG image
//     index: true,               // false → noindex,nofollow
//   });

import { useEffect } from "react";

const SITE_URL = "https://episodekit.net";
const DEFAULT_IMAGE = "/og/episodekit-og-home.jpg";

const setOrCreate = (selector, attrs) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement(attrs.tag || "meta");
    if (attrs.name) el.setAttribute("name", attrs.name);
    if (attrs.property) el.setAttribute("property", attrs.property);
    if (attrs.rel) el.setAttribute("rel", attrs.rel);
    document.head.appendChild(el);
  }
  if (attrs.content !== undefined) el.setAttribute("content", attrs.content);
  if (attrs.href !== undefined) el.setAttribute("href", attrs.href);
  return el;
};

const absolute = (url) => {
  if (!url) return null;
  if (/^https?:\/\//i.test(url)) return url;
  return `${SITE_URL}${url.startsWith("/") ? "" : "/"}${url}`;
};

export const useSEO = ({ title, description, path = "/", image, index = true }) => {
  useEffect(() => {
    const canonical = `${SITE_URL}${path}`;
    const ogImage = absolute(image || DEFAULT_IMAGE);

    if (title) document.title = title;

    if (description) {
      setOrCreate('meta[name="description"]', { name: "description", content: description });
      setOrCreate('meta[property="og:description"]', { property: "og:description", content: description });
      setOrCreate('meta[name="twitter:description"]', { name: "twitter:description", content: description });
    }
    if (title) {
      setOrCreate('meta[property="og:title"]', { property: "og:title", content: title });
      setOrCreate('meta[name="twitter:title"]', { name: "twitter:title", content: title });
    }

    setOrCreate('meta[property="og:url"]', { property: "og:url", content: canonical });
    setOrCreate('meta[name="twitter:url"]', { name: "twitter:url", content: canonical });
    setOrCreate('link[rel="canonical"]', { tag: "link", rel: "canonical", href: canonical });

    setOrCreate('meta[property="og:image"]', { property: "og:image", content: ogImage });
    setOrCreate('meta[name="twitter:image"]', { name: "twitter:image", content: ogImage });
    setOrCreate('meta[property="og:image:width"]', { property: "og:image:width", content: "1200" });
    setOrCreate('meta[property="og:image:height"]', { property: "og:image:height", content: "630" });

    setOrCreate('meta[name="robots"]', {
      name: "robots",
      content: index
        ? "index, follow, max-snippet:-1, max-image-preview:large"
        : "noindex, nofollow",
    });
  }, [title, description, path, image, index]);
};

export default useSEO;
